// Data source for /services and /services/$slug pages.
// Each entry powers a full SEO-optimised service page with H1, intro,
// H2/H3 structure, FAQ (with FAQPage schema), local trust and CTAs.

export type ServiceFaq = { q: string; a: string };

export type Service = {
  slug: string;
  title: string; // H1
  metaTitle: string; // <title> (<=60 chars)
  metaDescription: string; // <meta description> (<=160 chars)
  eyebrow: string;
  intro: string; // keyword rich lead paragraph
  whoWeHelp: string[];
  approach: { heading: string; body: string }[];
  benefits: string[];
  related: string[]; // slugs of related services
  faqs: ServiceFaq[];
};

export const SERVICES: Service[] = [
  {
    slug: "back-pain-physiotherapy",
    title: "Back Pain Physiotherapy in Centurion",
    metaTitle: "Back Pain Physiotherapist Centurion | Same Day Physio",
    metaDescription:
      "Trusted back pain physiotherapy in Centurion, Eco Boulevard, Highveld and Irene. Same day appointments, medical aid accepted. Book online today.",
    eyebrow: "Back Pain",
    intro:
      "Lower back pain, sciatica and stiffness are among the most common reasons patients visit our Centurion physiotherapy clinic. We combine a thorough assessment, hands on treatment and evidence based exercise so you can move, work and sleep without pain.",
    whoWeHelp: [
      "Office workers with chronic lower back pain",
      "Adults with sciatica and referred leg pain",
      "Patients recovering from disc bulges or prolapses",
      "Runners and gym goers with lumbar strain",
      "New mothers with postural back pain",
      "Anyone waking up with a stiff or aching back",
    ],
    approach: [
      { heading: "Detailed assessment", body: "A physiotherapist led movement, posture and neurological screen to pinpoint the true cause of your back pain, not just the symptom." },
      { heading: "Hands on treatment", body: "Joint mobilisation, soft tissue release, dry needling and taping to reduce pain and restore movement quickly." },
      { heading: "Targeted rehabilitation", body: "A personalised strengthening and mobility programme so pain does not return once treatment ends." },
      { heading: "Lifestyle and ergonomic advice", body: "Practical guidance on desk setup, sleeping position, lifting technique and safe return to exercise." },
    ],
    benefits: [
      "Faster relief from pain and stiffness",
      "Reduced risk of recurrence",
      "Improved posture and core strength",
      "Confidence to return to sport and daily life",
    ],
    related: ["neck-pain-physiotherapy", "sports-injury-physiotherapy", "dry-needling", "online-physiotherapy"],
    faqs: [
      { q: "How many physio sessions do I need for lower back pain?", a: "Most patients notice meaningful improvement within one to six sessions. Chronic back pain or disc related pain may need a structured programme over six to twelve weeks." },
      { q: "Do you treat sciatica in Centurion?", a: "Yes. We assess and treat sciatica caused by disc bulges, piriformis syndrome and nerve irritation using manual therapy, dry needling and progressive exercise." },
      { q: "Can I claim from medical aid?", a: "Yes. We submit claims directly to most major South African medical aids at scheme rates. A shortfall may apply." },
      { q: "Do I need a doctor's referral?", a: "No. Physiotherapy is a first line profession in South Africa, so you can book directly online or on WhatsApp without a referral." },
      { q: "Can you come to my home for back pain treatment?", a: "Yes. We offer home visit physiotherapy across Centurion and Midrand for patients who cannot travel." },
    ],
  },
  {
    slug: "neck-pain-physiotherapy",
    title: "Neck Pain Physiotherapy in Centurion",
    metaTitle: "Neck Pain Physiotherapist Centurion | Book Online",
    metaDescription:
      "Effective neck pain physiotherapy in Centurion for headaches, stiffness and postural pain. Same day bookings, medical aid rates. WhatsApp us today.",
    eyebrow: "Neck Pain",
    intro:
      "Neck pain, tension headaches and stiffness from long hours at a desk respond very well to physiotherapy. Our Centurion clinic assesses your posture, joints and muscles to relieve pain and prevent recurring problems.",
    whoWeHelp: [
      "Adults with tension or cervicogenic headaches",
      "Desk workers with chronic neck and shoulder pain",
      "Drivers and commuters with postural strain",
      "Patients with whiplash after motor vehicle accidents",
      "Anyone with pins and needles referring into the arm",
    ],
    approach: [
      { heading: "Neck and postural assessment", body: "A precise cervical spine, thoracic and shoulder assessment to identify what is driving your pain." },
      { heading: "Manual therapy and dry needling", body: "Gentle mobilisations, soft tissue work and trigger point dry needling for fast, drug free relief." },
      { heading: "Postural rehabilitation", body: "Deep neck flexor, scapular and thoracic mobility exercises tailored to your workspace and lifestyle." },
      { heading: "Workstation coaching", body: "Practical desk, screen and phone advice so pain does not creep back after long hours at work." },
    ],
    benefits: [
      "Fewer tension headaches",
      "Better posture and range of movement",
      "Reduced shoulder and arm referral",
      "Improved sleep quality",
    ],
    related: ["back-pain-physiotherapy", "sports-injury-physiotherapy", "dry-needling", "online-physiotherapy"],
    faqs: [
      { q: "Can physiotherapy help my tension headaches?", a: "Yes. Many headaches originate from the upper neck joints and muscles. Targeted manual therapy and postural rehab often reduce headache frequency significantly." },
      { q: "Is dry needling safe for neck pain?", a: "Yes. Dry needling is a safe, evidence based technique used by qualified physiotherapists to release tight muscles and trigger points." },
      { q: "How soon can I get an appointment?", a: "We offer same day physiotherapy appointments in Centurion when slots are available. Book online or WhatsApp us." },
      { q: "Do you treat whiplash after a car accident?", a: "Yes. We assess, treat and can provide medico legal reports for whiplash and soft tissue injuries from motor vehicle accidents." },
    ],
  },
  {
    slug: "sports-injury-physiotherapy",
    title: "Sports Injury Physiotherapist in Centurion",
    metaTitle: "Sports Physiotherapist Centurion | Injury & Rehab",
    metaDescription:
      "Sports physiotherapy in Centurion for runners, cyclists and gym athletes. Injury assessment, rehab and return to sport. Book online or WhatsApp.",
    eyebrow: "Sports Injuries",
    intro:
      "Whether you are a weekend runner, cyclist, gym goer or competitive athlete, sports injuries need accurate diagnosis and structured rehabilitation. Our Centurion sports physiotherapist helps you recover fully and return to sport stronger.",
    whoWeHelp: [
      "Runners with knee, calf, Achilles or shin pain",
      "Cyclists with lower back and knee pain",
      "Gym athletes with shoulder or elbow injuries",
      "Team sport players with ankle sprains and hamstring strains",
      "Youth athletes with growth related pain",
    ],
    approach: [
      { heading: "Sports specific assessment", body: "Movement analysis, strength testing and sport specific screening to identify the root cause of the injury." },
      { heading: "Hands on treatment", body: "Manual therapy, soft tissue release, dry needling, strapping and taping to reduce pain and get you moving." },
      { heading: "Progressive rehabilitation", body: "Sport specific loading, plyometrics and neuromuscular retraining to safely return you to full performance." },
      { heading: "Injury prevention", body: "Personalised strength, mobility and warm up programmes to reduce the risk of recurrence." },
    ],
    benefits: [
      "Faster return to training and competition",
      "Reduced risk of re injury",
      "Improved strength and biomechanics",
      "Confident, staged return to sport",
    ],
    related: ["orthopaedic-rehabilitation", "post-operative-rehabilitation", "back-pain-physiotherapy", "online-physiotherapy"],
    faqs: [
      { q: "Do you work with runners in Centurion?", a: "Yes. We regularly treat runners from Centurion, Midrand and surrounding areas for knee pain, IT band syndrome, plantar fasciitis, Achilles and shin issues." },
      { q: "How long before I can return to sport?", a: "Return to sport depends on the injury. Minor strains may resolve in one to two weeks; ligament, tendon or post surgical injuries require a structured multi week programme." },
      { q: "Do you offer return to sport testing?", a: "Yes. We use objective strength and functional criteria to guide a safe return to running, gym and team sport." },
    ],
  },
  {
    slug: "post-operative-rehabilitation",
    title: "Post Operative Physiotherapy in Centurion",
    metaTitle: "Post Op Physio Centurion | Knee, Hip & Shoulder Rehab",
    metaDescription:
      "Post operative physiotherapy in Centurion for knee, hip, shoulder and spine surgery. Structured rehab in clinic or at home. Medical aid accepted.",
    eyebrow: "Post Operative Rehab",
    intro:
      "Recovering well after orthopaedic surgery depends on structured, progressive physiotherapy. We work closely with your surgeon to guide you safely from the early post op phase back to full function.",
    whoWeHelp: [
      "Knee replacement and ACL reconstruction patients",
      "Hip replacement and hip arthroscopy patients",
      "Shoulder rotator cuff repair patients",
      "Spinal surgery patients",
      "Ankle and foot surgery patients",
    ],
    approach: [
      { heading: "Early post op care", body: "Swelling management, gentle mobilisation and safe activation within your surgeon's protocol." },
      { heading: "Progressive loading", body: "Structured strengthening, range of motion and proprioception work through each rehab phase." },
      { heading: "Functional retraining", body: "Walking, stairs, driving, work and sport specific tasks reintroduced at the right time." },
      { heading: "Home based options", body: "Home visit physiotherapy is available if travel is difficult in the early weeks after surgery." },
    ],
    benefits: [
      "Faster recovery of movement and strength",
      "Reduced pain and swelling",
      "Confidence to return to daily activities",
      "Better long term surgical outcomes",
    ],
    related: ["orthopaedic-rehabilitation", "home-physiotherapy", "sports-injury-physiotherapy"],
    faqs: [
      { q: "When should I start physiotherapy after surgery?", a: "Most patients benefit from starting physiotherapy within the first one to two weeks after surgery, guided by their surgeon's protocol." },
      { q: "Do you follow surgeon protocols?", a: "Yes. We work with your surgeon's rehabilitation protocol and adapt it to your progress and lifestyle." },
      { q: "Can you come to my home after surgery?", a: "Yes. Home visit physiotherapy is available across Centurion and Midrand for patients who cannot travel yet." },
    ],
  },
  {
    slug: "stroke-rehabilitation",
    title: "Stroke Rehabilitation Physiotherapy",
    metaTitle: "Stroke Rehab Physiotherapist Centurion & Pretoria",
    metaDescription:
      "Neurological physiotherapy for stroke recovery in Centurion and Midrand. In clinic or home visits. Regain movement, balance and independence.",
    eyebrow: "Stroke Rehabilitation",
    intro:
      "Recovery after a stroke is a journey. Our neurological physiotherapy helps you or your loved one regain movement, balance, walking and independence, whether at our Centurion clinic or in your home.",
    whoWeHelp: [
      "Adults recovering from ischaemic or haemorrhagic stroke",
      "Patients with hemiplegia or hemiparesis",
      "Patients with balance and walking difficulties",
      "Families needing caregiver support and training",
      "Long term stroke survivors seeking further progress",
    ],
    approach: [
      { heading: "Neurological assessment", body: "A full assessment of tone, strength, balance, gait and functional ability to set clear, measurable goals." },
      { heading: "Task specific training", body: "Repeated, meaningful practice of the movements and tasks that matter most to you." },
      { heading: "Gait and balance retraining", body: "Safe, progressive work on standing, walking and balance to reduce fall risk and improve confidence." },
      { heading: "Home and caregiver support", body: "Practical guidance on transfers, home setup and safe exercise for family members and caregivers." },
    ],
    benefits: [
      "Improved walking and mobility",
      "Better balance and reduced fall risk",
      "Greater independence at home",
      "Emotional and physical progress at every stage",
    ],
    related: ["home-physiotherapy", "post-operative-rehabilitation", "orthopaedic-rehabilitation"],
    faqs: [
      { q: "How soon after a stroke should physiotherapy start?", a: "Ideally as soon as the treating team clears the patient. Early, consistent rehabilitation is linked to better long term recovery." },
      { q: "Do you offer home visits for stroke patients?", a: "Yes. Home visit neurological physiotherapy is available in Centurion and Midrand, plus surrounding areas." },
      { q: "Can family members be involved in sessions?", a: "Absolutely. We actively involve family and caregivers so rehabilitation continues safely between sessions." },
    ],
  },
  {
    slug: "home-physiotherapy",
    title: "Home Visit Physiotherapy in Centurion, Pretoria & Midrand",
    metaTitle: "Home Visit Physiotherapist Centurion, Midrand & Pretoria",
    metaDescription:
      "Home physiotherapy in Centurion, Midrand and surrounding areas. Same day mobile physio for elderly, post op and neuro patients. Book online today.",
    eyebrow: "Home Physiotherapy",
    intro:
      "Home visit physiotherapy brings professional care to your door. Ideal for elderly patients, post operative recovery, stroke rehabilitation and anyone who finds it difficult to travel to a clinic.",
    whoWeHelp: [
      "Elderly patients with mobility difficulties",
      "Post surgical patients in the early weeks",
      "Stroke and neurological rehabilitation patients",
      "Busy professionals who prefer treatment at home or the office",
      "Families needing bedside care for a loved one",
    ],
    approach: [
      { heading: "Same day booking where possible", body: "We aim to offer same day home visits when a slot is available." },
      { heading: "Full clinical assessment at home", body: "A complete physiotherapy assessment in your own environment for the most relevant treatment plan." },
      { heading: "Environment specific rehabilitation", body: "We use your real stairs, bed, bathroom and living areas to make rehab practical and functional." },
      { heading: "Caregiver and family coaching", body: "Simple, safe strategies for daily transfers, exercise and fall prevention." },
    ],
    benefits: [
      "No travel required",
      "Treatment in your real environment",
      "Care for the whole family under one roof",
      "Ideal after surgery, stroke or during recovery",
    ],
    related: ["stroke-rehabilitation", "post-operative-rehabilitation", "orthopaedic-rehabilitation"],
    faqs: [
      { q: "Which areas do you cover for home visits?", a: "Centurion (Eco Park, Eco Boulevard, Highveld, Die Hoewes, Irene) and Midrand, plus surrounding areas. Contact us for other areas." },
      { q: "How much does a home visit cost?", a: "A home visit physiotherapy session is R1,050 and includes travel within our coverage area." },
      { q: "Do you treat elderly patients at home?", a: "Yes. Many of our home visit patients are elderly adults needing balance, strength and fall prevention rehabilitation." },
    ],
  },
  {
    slug: "dry-needling",
    title: "Dry Needling Therapy in Centurion",
    metaTitle: "Dry Needling Physio Centurion | Trigger Point Relief",
    metaDescription:
      "Dry needling in Centurion for muscle pain, tension and sports injuries. Safe, evidence based technique performed by a qualified physiotherapist.",
    eyebrow: "Dry Needling",
    intro:
      "Dry needling is a safe, evidence based technique used by qualified physiotherapists to release tight muscles and painful trigger points. It is a valuable part of treating back pain, neck pain, headaches and sports injuries.",
    whoWeHelp: [
      "Patients with chronic muscle tension",
      "Adults with tension headaches",
      "Runners and gym athletes with tight, overworked muscles",
      "Desk workers with shoulder and upper back knots",
      "Post surgical patients with muscle guarding",
    ],
    approach: [
      { heading: "What dry needling is", body: "Very fine, single use sterile needles inserted into muscle trigger points to release tension and reduce pain. It is different from acupuncture." },
      { heading: "When we use it", body: "As part of a broader treatment plan for musculoskeletal pain, alongside manual therapy and exercise." },
      { heading: "What to expect", body: "A brief, mild discomfort during needling and a possible local muscle twitch, followed by lasting relief for many patients." },
      { heading: "Safety first", body: "Dry needling is only performed by physiotherapists trained and certified in the technique." },
    ],
    benefits: [
      "Fast relief from muscle tension",
      "Fewer headaches",
      "Improved range of movement",
      "Complements manual therapy and exercise",
    ],
    related: ["back-pain-physiotherapy", "neck-pain-physiotherapy", "sports-injury-physiotherapy"],
    faqs: [
      { q: "Does dry needling hurt?", a: "Most patients feel only a mild pinprick and a brief muscle twitch. Any soreness usually settles within a day." },
      { q: "Is dry needling the same as acupuncture?", a: "No. Dry needling is a Western, evidence based physiotherapy technique targeting muscle trigger points, not traditional Chinese meridians." },
      { q: "How many sessions do I need?", a: "Many patients notice relief after one to three sessions, often combined with manual therapy and exercise." },
    ],
  },
  {
    slug: "orthopaedic-rehabilitation",
    title: "Orthopaedic Physiotherapy in Centurion",
    metaTitle: "Orthopaedic Physiotherapist Centurion | Joint Rehab",
    metaDescription:
      "Orthopaedic physiotherapy in Centurion for joint, ligament, tendon and post fracture rehabilitation. Book same day physio online or WhatsApp.",
    eyebrow: "Orthopaedic Rehab",
    intro:
      "Orthopaedic physiotherapy treats injuries and conditions of the muscles, joints, ligaments and tendons. Our Centurion clinic helps you recover from fractures, joint pain, arthritis and overuse injuries.",
    whoWeHelp: [
      "Patients recovering from fractures",
      "Adults with osteoarthritis of the knee, hip or shoulder",
      "Patients with tendon injuries such as tennis elbow or Achilles tendinopathy",
      "Ligament injuries including ankle sprains",
      "Patients with chronic joint pain and stiffness",
    ],
    approach: [
      { heading: "Full orthopaedic assessment", body: "A detailed assessment of joint range, strength, stability and movement patterns." },
      { heading: "Hands on treatment", body: "Manual therapy, soft tissue release, dry needling and taping to reduce pain and restore movement." },
      { heading: "Progressive rehabilitation", body: "Loading, strengthening and neuromuscular training tailored to your injury and goals." },
      { heading: "Return to function", body: "Practical, staged return to work, sport, gym and everyday activities." },
    ],
    benefits: [
      "Reduced joint pain and stiffness",
      "Improved strength and mobility",
      "Better long term joint health",
      "Confident return to activity",
    ],
    related: ["post-operative-rehabilitation", "sports-injury-physiotherapy", "back-pain-physiotherapy", "online-physiotherapy"],
    faqs: [
      { q: "Do you treat arthritis?", a: "Yes. Physiotherapy is one of the most effective non surgical treatments for osteoarthritis of the knee, hip and shoulder." },
      { q: "Can physio help tennis elbow?", a: "Yes. A combination of load management, hands on treatment and progressive strengthening usually resolves tennis and golfer's elbow." },
      { q: "How long does orthopaedic rehab take?", a: "Simple injuries may resolve in a few sessions. More complex or long standing conditions typically respond well over a structured six to twelve week programme." },
    ],
  },
  {
    slug: "chest-physiotherapy",
    title: "Chest Physiotherapy for Adults & Paediatrics",
    metaTitle: "Chest Physiotherapist Centurion | Adults & Babies",
    metaDescription:
      "Chest physiotherapy in Centurion for adults, babies and children. Phlegm clearance, breathing exercises, chest congestion relief and emergency home visits. Book online.",
    eyebrow: "Chest Physiotherapy",
    intro:
      "Our chest physiotherapy service focuses on helping adults and children clear phlegm, breathe more easily and recover from chest congestion, infection or surgery. For babies we use gentle, age appropriate techniques, including a suction machine when needed, to clear phlegm and support better breathing and sleep. Treatment is tailored to the patient's age, condition and clinical needs, and works alongside your doctor's medical care.",
    whoWeHelp: [
      "Adults: chest congestion, persistent phlegm, bronchitis, pneumonia recovery, COPD, bronchiectasis and asthma",
      "Adults: poor breathing efficiency, reduced chest expansion or weak cough",
      "Babies and children: chest congestion, difficulty clearing secretions, noisy breathing or poor feeding linked to respiratory illness",
      "Babies and children: bronchiolitis recovery, recurrent chest infections and post viral phlegm retention",
      "Patients needing post operative respiratory care after chest or abdominal surgery",
      "Anyone struggling with mucus retention and needing help to clear the chest, as advised by their doctor",
    ],
    approach: [
      { heading: "Adult chest physiotherapy", body: "A full respiratory assessment followed by hands on and exercise based techniques to loosen phlegm, improve cough effectiveness, expand the chest and make breathing feel easier." },
      { heading: "Paediatric chest physiotherapy", body: "Gentle, child friendly assessment and treatment for babies and children. We use positioning, percussion, vibration, assisted coughing and a suction machine when needed to help clear phlegm, with a parent or caregiver present at all times." },
      { heading: "Emergency phlegm clearance", body: "Urgent phlegm clearance is available after hours and on public holidays, at home or in clinic, for babies and adults who need immediate relief. A suction machine may be used to remove phlegm when clinically appropriate. An extra call out fee applies for after hours and holiday appointments." },
      { heading: "Breathing and chest mobility retraining", body: "Personalised breathing exercises, chest expansion work and posture advice to improve lung function, reduce breathlessness and lower the risk of recurring infection." },
    ],
    benefits: [
      "Improved phlegm clearance and easier breathing",
      "Better sleep and feeding for congested babies",
      "Reduced risk of chest infection and complications",
      "Care tailored to adults, babies and children",
      "Home visits and emergency after hours appointments available",
    ],
    related: ["home-physiotherapy", "post-operative-rehabilitation", "stroke-rehabilitation"],
    faqs: [
      { q: "What does chest physiotherapy do for babies?", a: "For babies, chest physiotherapy helps loosen and clear phlegm from the lungs using gentle positioning, percussion, vibration and a suction machine when needed. This can improve breathing, feeding and sleep, especially after bronchiolitis or a chest infection. A parent or caregiver is always present." },
      { q: "Do you offer emergency phlegm clearance after hours?", a: "Yes. We offer urgent phlegm clearance for babies and adults after hours and on public holidays, at home or in clinic. A suction machine may be used to remove phlegm when clinically appropriate. An additional call out fee applies for these appointments." },
      { q: "Can chest physiotherapy replace medical treatment?", a: "No. Chest physiotherapy supports medical care, it does not replace it. We encourage patients to remain under the care of their doctor or paediatrician, and we are happy to work alongside your treating medical team." },
      { q: "Do you offer home visits for chest physiotherapy?", a: "Yes. Home visit chest physiotherapy is available across Centurion and Midrand, which is often more comfortable for patients recovering from respiratory illness or surgery." },
      { q: "What does a chest physiotherapy session involve?", a: "A session starts with an assessment of breathing, chest movement and phlegm. Treatment may include breathing exercises, positioning, percussion, vibration, assisted coughing, suction to clear phlegm and chest mobility work. Every session is tailored to the patient's age and condition." },
      { q: "Do I need a referral for chest physiotherapy?", a: "No referral is required to book, although we welcome referrals from doctors, paediatricians and specialists and will liaise with them where appropriate." },
    ],
  },
  {
    slug: "online-physiotherapy",
    title: "Online Physiotherapy Consultations",
    metaTitle: "Online Physiotherapy South Africa | Virtual Physio",
    metaDescription:
      "Online physiotherapy consultations with a qualified, HPCSA registered physiotherapist. Video assessment, exercise programmes and advice. Book online today.",
    eyebrow: "Online Physiotherapy",
    intro:
      "Online physiotherapy, also known as telehealth physiotherapy, brings professional assessment and treatment to you over a secure video call. It is ideal for exercise based rehabilitation, follow up care, ergonomic advice and conditions that do not require hands on treatment, whether you are in Centurion, Midrand or anywhere else in South Africa.",
    whoWeHelp: [
      "Patients who live outside Centurion and Midrand",
      "Busy professionals who cannot easily travel to the clinic",
      "Follow up patients continuing an exercise programme",
      "Desk workers needing posture and ergonomic guidance",
      "Athletes wanting remote rehabilitation check ins",
      "Anyone needing prompt advice while waiting for an in person appointment",
    ],
    approach: [
      { heading: "Video assessment", body: "A detailed interview and guided movement assessment over video to understand your condition and set clear goals." },
      { heading: "Personalised exercise programme", body: "A tailored rehabilitation programme demonstrated live, then shared with you to follow between sessions." },
      { heading: "Ergonomic and lifestyle coaching", body: "Practical advice on your desk setup, daily habits and activity modification to support recovery." },
      { heading: "Progress reviews", body: "Structured online follow ups to progress your exercises, and referral to in clinic or home visit care when hands on treatment is needed." },
    ],
    benefits: [
      "Professional physio care from anywhere in South Africa",
      "No travel time or transport costs",
      "Same evidence based assessment and exercise prescription",
      "Easy transition to in clinic or home visit sessions if needed",
    ],
    related: ["back-pain-physiotherapy", "neck-pain-physiotherapy", "sports-injury-physiotherapy"],
    faqs: [
      { q: "Is online physiotherapy effective?", a: "Yes. Research shows that video based physiotherapy is effective for assessment, exercise prescription, education and rehabilitation of many musculoskeletal conditions, especially where a structured exercise programme is the main treatment." },
      { q: "What conditions can be treated with online physiotherapy?", a: "Back and neck pain, postural problems, sports injuries in later rehab stages, tendon pain, osteoarthritis management, and post operative exercise progression all work well online. If your condition needs hands on treatment, we will recommend an in clinic or home visit session." },
      { q: "What do I need for an online physiotherapy session?", a: "A smartphone, tablet or computer with a camera, a stable internet connection and enough space to move around. Wear comfortable clothing that lets you move freely and shows the affected area." },
      { q: "Can I claim an online physiotherapy consultation from medical aid?", a: "Most major South African medical aids reimburse telehealth physiotherapy consultations. We can provide the documentation needed for your claim." },
      { q: "How do I book an online physio session?", a: "Book directly through our online booking page or send us a WhatsApp message. No referral is needed. We will send you a secure video link before your appointment." },
      { q: "What happens if I need hands on treatment?", a: "If your assessment shows you would benefit from manual therapy or dry needling, we will arrange an in clinic session in Centurion or a home visit in Centurion, Midrand and surrounding areas." },
    ],
  },
];

export const SERVICES_BY_SLUG: Record<string, Service> = Object.fromEntries(
  SERVICES.map((s) => [s.slug, s]),
);
